import { Component, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';

import { SidebarNavComponent } from '../../components/sidebar-nav/sidebar-nav.component';
import { ToolbarNavComponent } from '../../components/toolbar-nav/toolbar-nav.component';
import { FooterComponent } from '../../components/footer/footer.component';
import { MobileSidebarDialogComponent } from '../../components/mobile-sidebar-dialog/mobile-sidebar-dialog.component';
import { BreadcrumbService, SkipLinkComponent } from '@twist/ui/components';
import { BREADCRUMB_LABEL_RESOLVER } from '@twist/ui-advanced-primitives';
import { LayoutStore } from '@twist/ui-core';

@Component({
  standalone: true,
  imports: [
    SidebarNavComponent,
    ToolbarNavComponent,
    RouterOutlet,
    FooterComponent,
    MobileSidebarDialogComponent,
    SkipLinkComponent
  ],
  // Bind the legacy `BreadcrumbService` (still living in
  // `@twist/ui/components` and consumed by 6 feature stores +
  // `PageTitleStrategy`) to the structural resolver token the
  // primitives `tw-page-header-breadcrumbs auto` consumes. This
  // crosses the type boundary at the layout level so primitives
  // never imports from components — keeping the project graph
  // acyclic. B6 Wave 2 removed the shell-rendered crumb strip, but
  // this provider MUST stay: every page now owns an in-header
  // `<tw-page-header-breadcrumbs auto>` (a child of this layout in the
  // injector tree) that resolves crumb labels through this token, and
  // the feature-store `setLabel(...)` overrides flow through the same
  // `BreadcrumbService`.
  providers: [
    { provide: BREADCRUMB_LABEL_RESOLVER, useExisting: BreadcrumbService },
  ],
  templateUrl: './portal-layout.component.html'
})
export class PortalLayoutComponent {
  private readonly layoutStore = inject(LayoutStore);

  /**
   * Current effective sidebar state — drives `.tw-layout[data-sidebar]`.
   * Reflects the user's configured `sidebarState` unless the viewport is below the
   * mobile breakpoint, in which case it resolves to `'hidden'` (and the mobile
   * dialog drawer, landing in Wave 6, takes over).
   */
  readonly effectiveSidebar = this.layoutStore.effectiveSidebarState;

  /**
   * Viewport-below-breakpoint flag — bound to `.tw-layout[data-viewport-mobile]`
   * as a belt-and-braces fallback. `effectiveSidebar()` already resolves to
   * `'hidden'` when this is true, but the attribute lets the grid column
   * collapse even if a consumer overrides `data-sidebar` manually.
   */
  readonly viewportBelowBreakpoint = this.layoutStore.viewportBelowBreakpoint;
}
